import {Injectable} from '@angular/core';
import * as detect from '../../diagram-render/helpers/shapes-detectors.util';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import {activityGraphChange} from './activity-graph-change';
import {textShapeGraphChange} from './text-shape-graph-change';
import {conTermGraphChange} from './con-term-graph-change';
import {GraphHelperService} from '../../diagram-render/helpers/graph-helper.service';
import {UndoService} from '../../commands/undo.service';
import {GeneralCommands} from '../../commands/general/general.commands';
import {stickerGraphChange} from './sticker-graph-change';
import {imageGraphChange} from './image-graph-change';
import {flowlineGraphChange} from './flowline-graph-change';
import {lineTextGraphChange} from './line-text-graph-change';
import {interactionGraphChange} from './interaction-graph-change';
import {DiagramCanvasService} from '../../diagram-render/diagram-canvas/diagram-canvas.service';
import {ActivityCommands} from '../../commands/activity/activity.commands';
declare let mxChildChange: any;
declare let mxVisibleChange: any;

@Injectable()
export class GraphEventsHandlingService {
  constructor(
    private registry: EditorRegistryService,
    private graphHelper: GraphHelperService,
    private undoSvc: UndoService,
    private generalCommands: GeneralCommands,
    private activityCommands: ActivityCommands,
    private diagramCanvasSvc: DiagramCanvasService
  ) {}

  handle(graph, evt, sender?) {
    if (this.diagramCanvasSvc.renderActive) {
      return;
    }

    const changes = evt.properties.changes || [];
    const done: any = {};
    const undoChanges = [];
    let skipUndo = false;

    // Find reordering changes changes and send them as a single group;
    const reorderActivityChanges = changes.filter(change => {
      return (
        change instanceof mxChildChange &&
        change.previous === change.parent &&
        detect.isActivity(change.child)
      );
    });
    const reorderCommands = reorderActivityChanges.reduce((acc, change) => {
      const cell = change.child;

      if (cell.skipUndo) {
        cell.skipUndo = false;
      } else {
        acc.push(
          this.activityCommands.reorder({
            mxKey: cell.id,
            position: change.index,
            previousPosition: change.previousIndex
          })
        );
      }

      return acc;
    }, []);

    if (reorderCommands.length > 0) {
      const command = this.generalCommands.group(reorderCommands);

      this.undoSvc.executeCommand(command);
    }

    // Graph changes (geometry, text, some styles like lock or rotation:
    changes.forEach((change, index) => {
      const cell = change.cell || null;

      if (!cell || (cell && done[cell.id])) {
        return;
      }

      if (change instanceof mxVisibleChange) {
        return;
      }

      // In case there is a graphical change, but there is no need to update server
      if (cell.skipUpdate) {
        done[cell.id] = true;
        cell.skipUpdate = false;

        return;
      }

      if (cell.skipUndo) {
        skipUndo = true;
        cell.skipUndo = false;
      }

      done[cell.id] = true;

      if (detect.isActivity(cell)) {
        undoChanges.push(activityGraphChange(graph, cell, change));
      }

      if (detect.isText(cell)) {
        undoChanges.push(textShapeGraphChange(graph, cell, change, this.graphHelper));
      }

      if (detect.isLineText(cell)) {
        undoChanges.push(lineTextGraphChange(graph, cell, change));
      }

      if (detect.isInteraction(cell)) {
        undoChanges.push(interactionGraphChange(graph, cell, change));
      }

      if (detect.isConnector(cell)) {
        undoChanges.push(conTermGraphChange(graph, cell, change, true));
      }

      if (detect.isTerminator(cell)) {
        undoChanges.push(conTermGraphChange(graph, cell, change, false));
      }

      if (detect.isFlowline(cell)) {
        undoChanges.push(flowlineGraphChange(graph, cell, change, changes)); // actually it should take all changes and probably last one
      }

      if (detect.isSticker(cell)) {
        undoChanges.push(stickerGraphChange(graph, cell, change));
      }

      if (detect.isImage(cell)) {
        undoChanges.push(imageGraphChange(graph, cell, change));
      }
    });

    if (undoChanges.length > 0) {
      const filtered = undoChanges.filter(c => c);

      if (filtered.length === 0) {
        return;
      }

      const command = this.generalCommands.change(filtered);

      this.undoSvc.executeCommand(command, skipUndo).then(() => {
        if (command.hasOverlayActions) {
          const overlayCommand = this.generalCommands.change(command.overlayActions, true);

          this.undoSvc.executeCommand(overlayCommand, skipUndo);
        }
      });
    }
  }
}
